import type {
    AgentInteractionState,
    OrchestratorAgent,
    OrchestratorState,
    SimpleAgentConfig
} from '@demo-portal/shared';
import React, { useEffect, useState } from 'react';

interface OrchestrationMetricsProps {
  orchestrator: OrchestratorAgent;
  agents: SimpleAgentConfig;
  orchestratorState: OrchestratorState;
  agentInteractions: Map<string, AgentInteractionState>;
  primaryColor?: string;
}

interface MetricData {
  label: string;
  value: number | string;
  unit?: string;
  trend?: 'up' | 'down' | 'stable';
  color?: string;
  icon?: string;
}

interface PerformanceMetric {
  agentId: string;
  agentName: string;
  avatar: string;
  responseTime: number;
  accuracy: number;
  efficiency: number;
  status: string;
}

const OrchestrationMetrics: React.FC<OrchestrationMetricsProps> = ({
  orchestrator,
  agents,
  orchestratorState,
  agentInteractions,
  primaryColor = '#667eea',
}) => {
  const [metrics, setMetrics] = useState<MetricData[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetric[]>([]);
  const [sessionStartTime] = useState(Date.now());

  // Calculate metrics based on orchestration state
  useEffect(() => {
    const newMetrics: MetricData[] = [];
    const newPerformanceMetrics: PerformanceMetric[] = [];

    // Overall system metrics
    const totalAgents = agents.length;
    const activeAgents = Array.from(agentInteractions.values()).filter(
      a => a.status === 'processing' || a.status === 'responding'
    ).length;
    const completedAgents = Array.from(agentInteractions.values()).filter(
      a => a.status === 'complete'
    ).length;
    const queuedTasks = orchestratorState.processingQueue.length;

    // Session duration
    const sessionDuration = Math.floor((Date.now() - sessionStartTime) / 1000);

    // System efficiency (completed / total * 100)
    const systemEfficiency = totalAgents > 0 ? Math.round((completedAgents / totalAgents) * 100) : 0;

    // Orchestrator status
    const orchestratorStatus = orchestratorState.status;

    newMetrics.push(
      {
        label: 'Total Agents',
        value: totalAgents,
        icon: '🤖',
        color: '#3498db',
      },
      {
        label: 'Active Agents',
        value: activeAgents,
        icon: '⚡',
        color: primaryColor,
        trend: activeAgents > 0 ? 'up' : 'stable',
      },
      {
        label: 'Completed',
        value: completedAgents,
        icon: '✅',
        color: '#27ae60',
        trend: completedAgents > 0 ? 'up' : 'stable',
      },
      {
        label: 'Queue Length',
        value: queuedTasks,
        icon: '📋',
        color: queuedTasks > 0 ? '#f39c12' : '#95a5a6',
        trend: queuedTasks > 0 ? 'up' : 'stable',
      },
      {
        label: 'System Efficiency',
        value: systemEfficiency,
        unit: '%',
        icon: '📊',
        color: systemEfficiency > 80 ? '#27ae60' : systemEfficiency > 50 ? '#f39c12' : '#e74c3c',
        trend: systemEfficiency > 70 ? 'up' : systemEfficiency > 30 ? 'stable' : 'down',
      },
      {
        label: 'Session Time',
        value: `${Math.floor(sessionDuration / 60)}:${(sessionDuration % 60).toString().padStart(2, '0')}`,
        icon: '⏱️',
        color: '#9b59b6',
      },
      {
        label: 'Orchestrator',
        value: orchestratorStatus.charAt(0).toUpperCase() + orchestratorStatus.slice(1),
        icon: '🎯',
        color: orchestratorStatus === 'processing' ? primaryColor : 
               orchestratorStatus === 'ready' ? '#27ae60' : '#95a5a6',
      }
    );

    // Individual agent performance metrics
    agents.forEach(agent => {
      const interaction = agentInteractions.get(agent.id);
      
      // Simulate performance metrics based on agent state
      const responseTime = interaction?.status === 'complete' 
        ? Math.floor(Math.random() * 2000) + 500  // 500-2500ms
        : interaction?.status === 'processing' 
        ? Math.floor(Math.random() * 1000) + 200  // 200-1200ms (ongoing)
        : 0;

      const accuracy = interaction?.status === 'complete' 
        ? Math.floor(Math.random() * 20) + 80     // 80-100%
        : interaction?.status === 'processing' 
        ? Math.floor(Math.random() * 15) + 75     // 75-90% (estimated)
        : 0;

      const efficiency = interaction?.progress || 0;

      newPerformanceMetrics.push({
        agentId: agent.id,
        agentName: agent.name,
        avatar: agent.avatar,
        responseTime,
        accuracy,
        efficiency,
        status: interaction?.status || 'idle',
      });
    });

    setMetrics(newMetrics);
    setPerformanceMetrics(newPerformanceMetrics);
  }, [agents, agentInteractions, orchestratorState, sessionStartTime, primaryColor]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return '#27ae60';
      case 'processing':
      case 'responding':
        return primaryColor;
      case 'called':
        return '#f39c12';
      default:
        return '#95a5a6';
    }
  };

  const getTrendIcon = (trend?: string) => {
    switch (trend) {
      case 'up':
        return '📈';
      case 'down':
        return '📉';
      default:
        return '➡️';
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      height: '500px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        fontSize: '1.3em',
        fontWeight: 'bold',
        marginBottom: '20px',
        color: '#2c3e50',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
      }}>
        <span style={{ fontSize: '1.2em' }}>📊</span>
        Orchestration Metrics
      </div>

      <div style={{
        height: '430px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}>
        {/* System Metrics Grid */}
        <div>
          <h3 style={{
            fontSize: '1em',
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span>⚙️</span>
            System Overview
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px',
          }}>
            {metrics.map((metric, index) => (
              <div
                key={index}
                style={{
                  background: `linear-gradient(135deg, ${metric.color}15, ${metric.color}05)`,
                  border: `1px solid ${metric.color}30`,
                  borderRadius: '12px',
                  padding: '12px',
                  textAlign: 'center',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{
                  fontSize: '1.5em',
                  marginBottom: '4px',
                }}>
                  {metric.icon}
                </div>
                
                <div style={{
                  fontSize: '1.4em',
                  fontWeight: 'bold',
                  color: metric.color,
                  marginBottom: '2px',
                }}>
                  {metric.value}{metric.unit}
                </div>
                
                <div style={{
                  fontSize: '0.75em',
                  color: '#7f8c8d',
                  fontWeight: '500',
                }}>
                  {metric.label}
                </div>

                {metric.trend && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    fontSize: '0.8em',
                  }}>
                    {getTrendIcon(metric.trend)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Agent Performance Metrics */}
        <div>
          <h3 style={{
            fontSize: '1em',
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span>🎯</span>
            Agent Performance
          </h3>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {performanceMetrics.map((agent, index) => (
              <div
                key={agent.agentId}
                style={{
                  background: '#f8f9fa',
                  border: `1px solid ${getStatusColor(agent.status)}30`,
                  borderRadius: '10px',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.3s ease',
                }}
              >
                {/* Agent Avatar */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${getStatusColor(agent.status)}, ${getStatusColor(agent.status)}dd)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2em',
                  border: `2px solid ${getStatusColor(agent.status)}`,
                }}>
                  {agent.avatar}
                </div>

                {/* Agent Info */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: 'bold',
                    color: '#2c3e50',
                    fontSize: '0.9em',
                    marginBottom: '2px',
                  }}>
                    {agent.agentName}
                  </div>
                  <div style={{
                    fontSize: '0.75em',
                    color: '#7f8c8d',
                    textTransform: 'capitalize',
                  }}>
                    Status: {agent.status}
                  </div>
                </div>

                {/* Performance Bars */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  minWidth: '120px',
                }}>
                  {/* Efficiency */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '0.7em', color: '#7f8c8d', minWidth: '50px' }}>
                      Progress:
                    </span>
                    <div style={{
                      flex: 1,
                      height: '4px',
                      background: '#ecf0f1',
                      borderRadius: '2px',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${agent.efficiency}%`,
                        height: '100%',
                        background: getStatusColor(agent.status),
                        borderRadius: '2px',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <span style={{ fontSize: '0.7em', color: '#7f8c8d', minWidth: '30px' }}>
                      {Math.round(agent.efficiency)}%
                    </span>
                  </div>

                  {/* Response Time */}
                  {agent.responseTime > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '0.7em', color: '#7f8c8d', minWidth: '50px' }}>
                        Response:
                      </span>
                      <span style={{ 
                        fontSize: '0.7em', 
                        color: agent.responseTime < 1000 ? '#27ae60' : agent.responseTime < 2000 ? '#f39c12' : '#e74c3c',
                        fontWeight: 'bold',
                      }}>
                        {agent.responseTime}ms
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrchestrationMetrics;